/**
 * B01 / H09-TECH — technical seller identity display shell.
 * BAN: invent G0-LEGAL-ROLE classification / responsibility-split copy.
 * Not Wave1 Home H09-UI; not shippingLightEnabled-gated.
 * GoPay MoR G13 — mailto/tel from public contactEmail / contactPhone when present.
 */
import { useTranslation } from 'react-i18next';
import { formatSellerLegalAddress } from './formatSellerLegalAddress.js';
import { usePublicSellerLegalQuery } from './publicSellerLegalApi.js';

export interface SellerLegalIdentityShellProps {
  /**
   * Tenant whose APPROVED org identity to show.
   * Omit to use route tenant (shop / checkout). Pass offer.tenantContext.tenantCode on browse.
   */
  readonly apiTenantCode?: string | null;
  /** Optional test id prefix (default `customer-seller-legal`). */
  readonly testIdPrefix?: string;
  /** Compact layout for offer detail / tight checkout slots. */
  readonly compact?: boolean;
}

function MetaRow(props: {
  readonly label: string;
  readonly value: string;
  readonly testId: string;
  readonly href?: string | null;
}): JSX.Element {
  const { label, value, testId, href } = props;
  return (
    <div className="flex items-start justify-between gap-4 py-1.5 first:pt-0 last:pb-0">
      <span className="shrink-0 text-xs font-medium text-[var(--color-on-surface-muted)]">
        {label}
      </span>
      {href != null && href.length > 0 ? (
        <a
          href={href}
          className="min-w-0 break-words text-end text-sm leading-snug text-[var(--color-accent)] underline underline-offset-2"
          data-testid={testId}
        >
          {value}
        </a>
      ) : (
        <span
          className="min-w-0 break-words text-end text-sm leading-snug text-[var(--color-on-surface)]"
          data-testid={testId}
        >
          {value}
        </span>
      )}
    </div>
  );
}

function normalizeContactEmail(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string') {
    return null;
  }
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function normalizeContactPhone(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string') {
    return null;
  }
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function telHref(phone: string): string {
  const digits = phone.replace(/[^\d+]/g, '');
  return `tel:${digits.length > 0 ? digits : phone}`;
}

export function SellerLegalIdentityShell({
  apiTenantCode = null,
  testIdPrefix = 'customer-seller-legal',
  compact = false,
}: SellerLegalIdentityShellProps): JSX.Element | null {
  const { t } = useTranslation('customer');
  const query = usePublicSellerLegalQuery(apiTenantCode);
  const data = query.data;

  if (data == null) {
    return null;
  }

  const addressLine = formatSellerLegalAddress(data.registeredAddress);
  const contactEmail = normalizeContactEmail(data.contactEmail);
  const contactPhone = normalizeContactPhone(data.contactPhone);

  return (
    <section
      className={
        compact
          ? 'flex min-w-0 flex-col gap-1.5'
          : 'flex min-w-0 flex-col gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-surface-elevated)] px-3 py-3'
      }
      data-testid={testIdPrefix}
      aria-label={t('sellerLegal.ariaLabel')}
    >
      <h3
        className="m-0 text-xs font-semibold uppercase tracking-wide text-[var(--color-on-surface-muted)]"
        data-testid={`${testIdPrefix}-heading`}
      >
        {t('sellerLegal.heading')}
      </h3>
      <div
        className={
          compact
            ? 'rounded-xl border border-[var(--color-outline-variant)] bg-[var(--color-surface)] px-3 py-1'
            : undefined
        }
        data-testid={`${testIdPrefix}-fields`}
      >
        <MetaRow
          label={t('sellerLegal.legalName')}
          value={data.legalName}
          testId={`${testIdPrefix}-legal-name`}
        />
        <div className="border-t border-[var(--color-outline-variant)]" aria-hidden="true" />
        <MetaRow label={t('sellerLegal.ico')} value={data.ico} testId={`${testIdPrefix}-ico`} />
        {data.dic != null ? (
          <>
            <div className="border-t border-[var(--color-outline-variant)]" aria-hidden="true" />
            <MetaRow label={t('sellerLegal.dic')} value={data.dic} testId={`${testIdPrefix}-dic`} />
          </>
        ) : null}
        {addressLine != null ? (
          <>
            <div className="border-t border-[var(--color-outline-variant)]" aria-hidden="true" />
            <MetaRow
              label={t('sellerLegal.registeredAddress')}
              value={addressLine}
              testId={`${testIdPrefix}-address`}
            />
          </>
        ) : null}
        {contactEmail != null ? (
          <>
            <div className="border-t border-[var(--color-outline-variant)]" aria-hidden="true" />
            <MetaRow
              label={t('sellerLegal.contactEmail')}
              value={contactEmail}
              href={`mailto:${contactEmail}`}
              testId={`${testIdPrefix}-contact-email`}
            />
          </>
        ) : null}
        {contactPhone != null ? (
          <>
            <div className="border-t border-[var(--color-outline-variant)]" aria-hidden="true" />
            <MetaRow
              label={t('sellerLegal.contactPhone')}
              value={contactPhone}
              href={telHref(contactPhone)}
              testId={`${testIdPrefix}-contact-phone`}
            />
          </>
        ) : null}
      </div>
    </section>
  );
}
